// Main application bootstrap with Alpine.js
// @ts-expect-error Alpine global
import Alpine from 'alpinejs';
import { Router } from './app/router';
import { ScreenController } from './ui/screenController';
import { store } from './app/store';
import { initAnalytics } from './app/analytics';
import { initStatsig, FLAGS } from './app/statsig';
import { initMetaPixel } from './infra/metaPixel';
import localeData from './config/locale.en.json';
import { waitForConsent, initAnalytics as initConsentedAnalytics, initMarketing } from './utils/consent';
import { initConsentAlpineData } from './domain/consent';
import { PreloaderService } from './utils/preloader';
import { FLOW_CONFIG } from './config/flow';

// Make Alpine available globally
window.Alpine = Alpine;

import { initGlobe } from './components/Globe';
declare global {
  interface Window {
    initGlobe: typeof initGlobe;
    Alpine: any;
    lunaiDebug: any;
  }
}
window.initGlobe = initGlobe;

// Preserve static HTML text when localization misses a key
document.addEventListener('alpine:init', () => {
  Alpine.directive('text', (el, { expression }, { effect, evaluateLater }) => {
    const evaluate = evaluateLater(expression);
    const defaultText = el.textContent ?? '';
    effect(() => {
      evaluate((value: unknown) => {
        if (value === undefined || value === null || value === '') {
          el.textContent = defaultText;
        } else {
          el.textContent = String(value);
        }
      });
    });
  });

  // Water intake screen component
  Alpine.data('waterIntakeScreen', () => ({
    selectedGlasses: 0,

    selectGlass(glassNumber: number) {
      // If clicking on the same glass that's the current max, toggle it off
      if (this.selectedGlasses === glassNumber) {
        this.selectedGlasses = glassNumber - 1;
      } else {
        this.selectedGlasses = glassNumber;
      }
    },

    submitSelection() {
      if (this.selectedGlasses > 0) {
        // Store the value (navigation is handled by data-action="next")
        store.set('water_intake', this.selectedGlasses);
      }
    }
  }));
});

// Application initialization
async function boot() {
  console.log('🚀 Lunai Care Web2App Starting...');

  if (window.location.hash === '#success' && (new URLSearchParams(window.location.search)).get('redirect_status') === 'failed') {
    console.error('Setup intent failed');
    window.location.hash = '#subscription';
  }


  // Get preloader element
  const preloader = document.getElementById('preloader');

  try {
    // Show preloader is already visible by default

    // Load localization data
    Alpine.store('locale', localeData);

    // Initialize consent management store
    initConsentAlpineData();

    // Initialize Alpine stores
    // Load existing user data from store
    const existingData = store.getAll() || {};
    console.log('Initializing Alpine userData store with:', existingData);
    Alpine.store('userData', Alpine.reactive(existingData));
    Alpine.store('flags', {});

    // Keep Alpine store in sync with regular store
    store.on((state) => {
      const alpineStore = Alpine.store('userData');
      Object.keys(state).forEach(key => {
        if (alpineStore[key] !== state[key]) {
          alpineStore[key] = state[key];
          console.log(`Synced Alpine userData.${key} = ${JSON.stringify(state[key])}`);
        }
      });
    });

    // Start Alpine
    Alpine.start();

    // Get app container
    const appContainer = document.getElementById('app');
    if (!appContainer) {
      console.error('App container not found');
      hidePreloader(preloader);
      return;
    }

    // Get or create user ID
    const userId = getOrCreateUserId();

    // Wait for consent decision before initializing tracking services
    waitForConsent(async (consent) => {
      if (consent.analytics) {
        // Initialize analytics services that require consent
        await initAnalytics();
        await initStatsig(userId);
        initConsentedAnalytics();
      }

      if (consent.marketing) {
        // Initialize marketing services that require consent
        await initMetaPixel();
        initMarketing();
      }
    });

    // Initialize essential services (no consent required) with minimum delay
    await Promise.all([
      // Add minimum display time for preloader (1.5 seconds)
      new Promise(resolve => setTimeout(resolve, 1500))
    ]);

    // Initialize screen controller
    const screenController = new ScreenController(appContainer);

    // Initialize router
    const router = new Router(screenController);

    // Initialize preloader service
    const preloaderService = new PreloaderService();
    preloaderService.initialize(FLOW_CONFIG);

    // Process onload preloads in background (non-blocking)
    preloaderService.processOnloadPreloads().catch(console.error);

    // Provide preloader to router
    router.setPreloaderService(preloaderService);

    // Set up navigation event listeners using PRD pattern
    document.addEventListener('ui:navigate', (event) => {
      const direction = event.detail?.direction;
      if (direction === 'next') {
        router.navigateNext();
      } else if (direction === 'back') {
        router.navigateBack();
      } else if (event.detail?.screenId) {
        router.navigateTo(event.detail.screenId);
      }
    });

    // Provide current screen info to screen controller
    document.addEventListener('app:getCurrentScreen', (event) => {
      event.detail = { screen: router.getCurrentScreen() };
    });

    // Track screen views
    document.addEventListener('app:screenView', (event) => {
      console.log('Screen view:', event.detail);
      // Send to analytics service
    });

    // Handle timer expiration
    document.addEventListener('app:timerExpired', () => {
      handleTimerExpired();
    });

    // Start router
    router.init();

    // Hide preloader after everything is initialized
    hidePreloader(preloader);

    // Log initial state
    console.log('📱 App initialized successfully');
    console.log('📊 User data:', store.getAll());
  } catch (error) {
    console.error('Error during initialization:', error);
    // Hide preloader even on error
    hidePreloader(preloader);
  }
}

// Hide preloader with animation
function hidePreloader(preloader: HTMLElement | null) {
  if (preloader) {
    preloader.classList.add('hidden');
    // Remove from DOM after animation completes
    setTimeout(() => {
      preloader.style.display = 'none';
    }, 500);
  }
}

// Handle timer expiration
function handleTimerExpired() {
  // Could show different pricing or messaging
  console.log('⏰ Timer expired - showing regular pricing');
}

// Get or create anonymous user ID
function getOrCreateUserId() {
  const key = 'lunai_user_id';
  let userId = localStorage.getItem(key);

  if (!userId) {
    userId = 'user_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    localStorage.setItem(key, userId);
  }

  return userId;
}

// Wait for DOM to be ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}

// Export for debugging
window.lunaiDebug = {
  store,
  getState: () => store.getAll(),
  clearState: () => store.clear(),
  navigateTo: (screenId) => {
    document.dispatchEvent(new CustomEvent('navigation:to', { detail: { screenId } }));
  }
};
